import { useMemo, useState } from "react";
import { NOTICE } from "@/lib/statutory";

export function NoticePeriodCalculator() {
  const [yearsService, setYearsService] = useState(3);
  const [monthsService, setMonthsService] = useState(0);

  const result = useMemo(() => {
    const totalMonths = yearsService * 12 + monthsService;
    if (totalMonths < NOTICE.qualifyingMonths) {
      return { eligible: false, employer: 0, employee: 0 };
    }
    const completeYears = Math.floor(yearsService);
    const employer =
      completeYears < 2
        ? NOTICE.employerMinWeeks
        : Math.min(completeYears * NOTICE.employerWeeksPerYear, NOTICE.employerWeeksCap);
    return { eligible: true, employer, employee: NOTICE.employeeMinWeeks };
  }, [yearsService, monthsService]);

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <NumField label="Complete years of service" value={yearsService} onChange={setYearsService} min={0} max={50} />
        <NumField label="Additional months" value={monthsService} onChange={setMonthsService} min={0} max={11} />
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl bg-brand p-6 text-brand-foreground">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">
            Notice from employer
          </p>
          <p className="mt-3 font-serif text-4xl">
            {result.eligible ? `${result.employer} week${result.employer === 1 ? "" : "s"}` : "None"}
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-background p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Notice from employee
          </p>
          <p className="mt-3 font-serif text-4xl text-brand">
            {result.eligible ? `${result.employee} week` : "None"}
          </p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            Under section 86 of the Employment Rights Act 1996, an employer must give at least 1 week's notice after 1
            month's service, then 1 week per complete year of service after 2 years, capped at {NOTICE.employerWeeksCap}{" "}
            weeks. Employees owe at least 1 week's notice after 1 month's service.
          </p>
          <p>Contractual notice may be longer, the higher of statutory and contractual applies.</p>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, min, max }: { label: string; value: number; onChange: (n: number) => void; min?: number; max?: number }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
